/**
 * THE action component family (R2, 2026-07-29) — one place that knows what a
 * button in this app looks like, so a surface declares WHAT it offers and never
 * re-invents the chrome.
 *
 * Four variants, matching the four jobs the surfaces actually do:
 *   - `primary`   — the one filled CTA of a step (»Analyse starten«). Shares the
 *                   `.btn-primary` geometry; `--control` carries the fill.
 *   - `secondary` — the outlined companion (»Alle auswählen«, »Bestätigen«).
 *   - `quiet`     — icon + label, pill outline, no ground (today's `.ws-iconbtn`).
 *   - `icon-only` — the same quiet chrome with the label MOVED to `aria-label`.
 *                   {@link action} enforces this: an icon-only action without a
 *                   label throws, because an unnamed control is a defect.
 * Plus {@link materialTile} — the file-picker "material" tile, a genuine
 * affordance §0 sanctions as bounded (a drop zone must show where to drop).
 *
 * Colour roles (three-role model): `--control` carries every interactive
 * affordance; `--brand` is mark-only and never fills a button; `--good/--warn/
 * --critical` stay semantic, so an action never borrows them for emphasis.
 * Hover is CALM (`6615766`): the outline firms up or the ink shifts — no lift,
 * no shadow appearing, no `--control` ground washed across a structural row.
 *
 * Every action carries a registered `data-act` verb (AX-3) and, for the
 * payload-bearing verbs, `data-*` from {@link ActionPayload}. `is-pending` is the
 * registered in-flight state class; `?disabled` is the real DOM attribute so
 * keyboard and AT see the same truth the pixels do.
 *
 * CSS: the `§6 ACTION FAMILY` block in `index.html` (below the §12 containment
 * primitives it composes).
 */

import { classMap, html, ifDefined, nothing, type TemplateResult } from "@platform/web-kit/render"
import { icon, type IconName } from "./icons"

/** The four action shapes. `icon-only` REQUIRES a label — it becomes the aria-label. */
export type ActionVariant = "primary" | "secondary" | "quiet" | "icon-only"

/**
 * The verbs the surfaces actually offer, mapped to their glyph — so »entfernen«
 * is the same mark on the Nachweis row, the asset inventory and the CV proposal.
 * Pass `icon` explicitly to override; pass neither for a label-only action.
 */
export type ActionIntent =
  | "add"
  | "remove"
  | "upload"
  | "download"
  | "edit"
  | "confirm"
  | "discard"
  | "refresh"
  | "forward"
  | "expand"

const INTENT_ICON: Record<ActionIntent, IconName> = {
  add: "plus",
  remove: "trash",
  upload: "upload",
  download: "download",
  edit: "pencil",
  confirm: "check",
  discard: "x",
  refresh: "refresh",
  forward: "chevronRight",
  expand: "chevronDown",
}

/**
 * The `data-*` payload the delegated listener hands the verb's handler
 * (`installActionDelegate` snapshots the whole `dataset`).
 *
 * lit-html templates are STATIC — there is no attribute spread — so the payload
 * is a NAMED slot vocabulary rather than an open map. These are the keys the
 * app's payload-bearing verbs use; adding a verb with a new key means adding one
 * `ifDefined` line below, and that visibility is the point: a payload key is
 * part of the AX contract, not an incidental string. An action whose payload
 * genuinely cannot fit here writes its own element and composes
 * {@link actionClass} + {@link actionBody} instead.
 */
export interface ActionPayload {
  /** `data-url` — a nav target (English code, AX-9). */
  url?: string
  /** `data-kind` — a material / asset kind discriminator. */
  kind?: string
  /** `data-id` — the generic identity slot. */
  id?: string
  /** `data-asset-id` — a Nachweis. */
  assetId?: string
  /** `data-ingest-id` — an ingest run. */
  ingestId?: string
  /** `data-document-id` — one document within an ingest run. */
  documentId?: string
  /** `data-claim-id` — an evidence claim. */
  claimId?: string
  /** `data-application` — an application (bare id, not the URN). */
  application?: string
  /** `data-opportunity` — a Stelle. */
  opportunity?: string
  /** `data-value` — a free scalar (a chosen option, a filter). */
  value?: string
}

/** One action. `label` is ALREADY-RESOLVED German (the surface resolves through `t()`). */
export interface ActionSpec {
  /** The registered `data-act` verb (AX-3). */
  act: string
  /** The German label — visible text, or the accessible name when `icon-only`. */
  label: string
  /** Default `secondary`. */
  variant?: ActionVariant
  /** Picks the glyph from the shared verb→icon map unless `icon` overrides it. */
  intent?: ActionIntent
  /** An explicit glyph (wins over `intent`). */
  icon?: IconName
  payload?: ActionPayload
  disabled?: boolean
  /** In flight — renders the registered `is-pending` state and disables the control. */
  pending?: boolean
  /** German copy shown WHILE pending (»wird hochgeladen …«). Falls back to `label`. */
  pendingLabel?: string
  /** Extra classes for a surface's own layout (never for chrome — chrome is the variant). */
  className?: string
  /** A native tooltip, when the label alone is not the whole story. */
  title?: string
}

const VARIANT_CLASS: Record<ActionVariant, string> = {
  primary: "act-primary btn-primary",
  secondary: "act-secondary btn-secondary",
  quiet: "act-quiet",
  "icon-only": "act-icon",
}

/**
 * The class list for an action's chrome — for callers that must own the element
 * itself (an exotic `data-*` payload, an `<a>` instead of a `<button>`, a `<label>`
 * wrapping a real file input). Pair it with {@link actionBody}.
 */
export function actionClass({ variant = "secondary", pending = false, className }: Pick<ActionSpec, "variant" | "pending" | "className">): string {
  return ["act", VARIANT_CLASS[variant], pending ? "is-pending" : "", className ?? ""].filter(Boolean).join(" ")
}

/** The inside of an action: the glyph (when any) and the label (hidden when `icon-only`). */
export function actionBody({ label, variant = "secondary", intent, icon: name, pending = false, pendingLabel }: ActionSpec): TemplateResult {
  const glyph = name ?? (intent ? INTENT_ICON[intent] : undefined)
  const text = pending ? (pendingLabel ?? label) : label
  return html`${glyph ? icon(glyph) : nothing}${variant === "icon-only" ? nothing : html`<span class="act-label" lang="de">${text}</span>`}`
}

/**
 * Render one action as a real `<button>` (AX-3: an affordance is a button or a
 * link, never a clickable div).
 *
 * @throws when an `icon-only` action carries no label — it would ship an unnamed control.
 */
export function action(spec: ActionSpec): TemplateResult {
  const { act, label, variant = "secondary", payload = {}, disabled = false, pending = false, title } = spec
  if (variant === "icon-only" && label.trim() === "") {
    throw new Error(`[career] icon-only action "${act}" needs a label — it becomes the aria-label.`)
  }
  return html`<button
    class=${actionClass(spec)}
    data-act=${act}
    data-url=${ifDefined(payload.url)}
    data-kind=${ifDefined(payload.kind)}
    data-id=${ifDefined(payload.id)}
    data-asset-id=${ifDefined(payload.assetId)}
    data-ingest-id=${ifDefined(payload.ingestId)}
    data-document-id=${ifDefined(payload.documentId)}
    data-claim-id=${ifDefined(payload.claimId)}
    data-application=${ifDefined(payload.application)}
    data-opportunity=${ifDefined(payload.opportunity)}
    data-value=${ifDefined(payload.value)}
    aria-label=${ifDefined(variant === "icon-only" ? label : undefined)}
    title=${ifDefined(title)}
    ?disabled=${disabled || pending}
    lang="de"
  >
    ${actionBody(spec)}
  </button>`
}

/**
 * The file-picker "material" tile — a bounded drop target (§0 sanctions the drop
 * zone as a genuine affordance): a dashed square edge, the glyph, WHAT this slot
 * takes, and an honest hint that flips to the in-flight copy while uploading.
 *
 * This is the shape the CV-Werkbank's »Unterlagen hinzufügen« band is built from.
 */
export interface MaterialTileSpec {
  act: string
  /** What this slot takes (»Lebenslauf«, »Zeugnisse«) — German, resolved. */
  label: string
  /** The one-line honest hint under it (»Datei wählen« / »wird hochgeladen …«). */
  hint: string
  icon?: IconName
  payload?: ActionPayload
  disabled?: boolean
  pending?: boolean
}

export function materialTile({ act, label, hint, icon: name = "upload", payload = {}, disabled = false, pending = false }: MaterialTileSpec): TemplateResult {
  return html`<button
    class=${classMap({ act: true, "act-material": true, "is-pending": pending })}
    data-act=${act}
    aria-busy=${ifDefined(pending ? "true" : undefined)}
    data-url=${ifDefined(payload.url)}
    data-kind=${ifDefined(payload.kind)}
    data-id=${ifDefined(payload.id)}
    ?disabled=${disabled}
    lang="de"
  >
    <span class="act-material-icon" aria-hidden="true">${icon(name)}</span>
    <span class="act-material-label" lang="de">${label}</span>
    <span class="act-material-hint" lang="de">${hint}</span>
  </button>`
}
